<template>
  <div
    class="animate-fade-in min-h-screen bg-color-background text-color-text-primary font-sans"
  >
    <div
      v-if="heroSlides.length > 0"
      class="relative w-full overflow-hidden sm:mb-4"
    >
      <swiper
        :modules="[Pagination, Autoplay]"
        :slides-per-view="1"
        :space-between="0"
        :loop="heroSlides.length > 1"
        :autoplay="{
          delay: 3000,
          disableOnInteraction: false,
        }"
        :pagination="{
          clickable: true,
          dynamicBullets: false,
        }"
        :grab-cursor="true"
        :simulate-touch="true"
        :touch-ratio="1"
        :touch-angle="45"
        :resistance="true"
        :resistance-ratio="0.85"
        @swiper="onSwiper"
        @slide-change="onSlideChange"
        class="hero-swiper w-full h-[30vh] sm:h-[45svh]"
      >
        <swiper-slide
          v-for="(slide, index) in heroSlides"
          :key="index"
          class="p-[15px] sm:p-0"
        >
          <a
            class="relative block w-full h-full"
            :href="getFullUrl(slide.link)"
            target="_blank"
          >
            <img
              :src="slide.image_url"
              @error="handleBannerImageError($event, slide)"
              class="w-full h-full object-cover rounded-[20px] sm:rounded-none pointer-events-none"
              loading="eager"
              fetchpriority="high"
              draggable="false"
            />
          </a>
        </swiper-slide>
      </swiper>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-10">
      <div
        class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-footer"
      ></div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="container-custom py-6 text-center">
      <p class="text-red-500 text-sm mb-3">{{ error }}</p>
      <button
        v-if="
          error.includes('Slug') || error.includes('اطلاعات فروشگاه یافت نشد')
        "
        @click="goToLoginOrHome"
        class="px-3 py-1.5 border border-color-primary text-color-primary rounded hover:bg-color-primary hover:text-white transition text-xs"
      >
        بازگشت
      </button>
      <button
        v-else
        @click="initData"
        class="px-3 py-1.5 border border-color-primary text-color-primary rounded hover:bg-color-primary hover:text-white transition text-xs"
      >
        تلاش مجدد
      </button>
    </div>

    <!-- Content Sections -->
    <div v-else>
      <!-- دسته‌بندی‌های ویژه -->
      <section class="container-custom">
        <h2 class="text-xl md:text-2xl font-bold text-center mb-4">
          <span class="text-color-secondary">دسته‌بندی‌ها</span>
        </h2>

        <swiper
          :modules="[Navigation, Autoplay]"
          :slides-per-view="3.5"
          :space-between="12"
          :navigation="false"
          :grab-cursor="true"
          :loop="categories.length > 8"
          :autoplay="{
            delay: 2500,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }"
          :breakpoints="{
            '480': { slidesPerView: 4.5 },
            '640': { slidesPerView: 5.5 },
            '768': { slidesPerView: 6 },
            '1024': { slidesPerView: 7 },
            '1280': { slidesPerView: 8 },
          }"
          class="category-swiper"
        >
          <swiper-slide v-for="cat in categories" :key="cat.id">
            <router-link
              :to="`/category/${cat.slug}`"
              class="flex flex-col items-center w-full"
            >
              <div
                class="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden mb-3 border-2 border-transparent hover:border-color-secondary transition-colors flex-shrink-0"
              >
                <img
                  :src="cat.icon || Defaultphoto"
                  @error="handleImageError($event, cat.icon)"
                  class="w-full h-full object-cover hover:scale-110 transition-transform duration-500 pointer-events-none"
                />
              </div>

              <h3
                class="text-xs md:text-sm font-semibold text-gray-600 hover:text-color-secondary text-center leading-tight"
              >
                {{ cat.name }}
              </h3>
            </router-link>
          </swiper-slide>
        </swiper>
      </section>

      <!-- بخش بنرها -->
      <div class="w-full lg:w-[90%] p-[15px] sm:p-0" style="margin: 5px auto">
        <!-- دو بنر اول (small-1 و small-2) -->
        <section
          v-if="doubleBanners.length > 0"
          class="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-4 px-1"
        >
          <a
            :href="getFullUrl(banner.link)"
            target="_blank"
            v-for="banner in doubleBanners"
            :key="banner.id"
            class="relative rounded-[20px] overflow-hidden group cursor-pointer aspect-[32/15]"
          >
            <img
              :src="banner.image_url"
              @error="handleBannerImageError($event, banner)"
              class="w-full h-full group-hover:scale-105 transition duration-500"
            />
          </a>
        </section>

        <!-- چهار بنر مینی اسمال (small-1 تا small-4) -->
        <section v-if="miniBanners.length > 0" class="my-4 px-1">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <a
              :href="getFullUrl(banner.link)"
              target="_blank"
              v-for="banner in miniBanners"
              :key="banner.id"
              class="relative rounded-xl overflow-hidden group cursor-pointer aspect-[4/3]"
            >
              <img
                :src="banner.image_url"
                @error="handleBannerImageError($event, banner)"
                class="w-full h-full object-cover group-hover:scale-105 transition duration-500"
              />
              <div
                class="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end p-2"
              >
                <h3
                  class="text-xs md:text-sm font-bold text-white line-clamp-2"
                  style="text-shadow: 0 0 4px black"
                >
                  {{ banner.title }}
                </h3>
              </div>
            </a>
          </div>
        </section>

        <!-- بنر استاتیک بزرگ -->
        <section
          v-if="staticBanner"
          class="my-4 px-1 max-h-[300px] rounded-[20px]"
        >
          <a
            class="relative rounded-[20px] overflow-hidden aspect-[21/5]"
            :href="getFullUrl(staticBanner.link)"
            target="_blank"
          >
            <img
              :src="staticBanner.image_url"
              @error="handleBannerImageError($event, staticBanner)"
              class="w-full max-h-[300px] rounded-[20px] object-cover"
            />
          </a>
        </section>
      </div>

      <!-- Special Offers Section -->
      <section
        v-if="activeCampaign && specialOffers.length > 0"
        id="offers"
        class="py-6 bg-color-text-secondary my-4 relative overflow-hidden"
        style="background-color: var(--color-gray-footer)"
      >
        <div class="container-custom relative z-10">
          <div
            class="flex flex-col md:flex-row justify-between items-center mb-4 gap-3"
          >
            <div class="text-center md:text-right">
              <h2
                class="text-2xl md:text-3xl font-bold mb-1"
                style="color: var(--color-background-2)"
              >
                {{ activeCampaign.name }}
              </h2>
              <p class="text-landing-card text-xs">فرصت رو از دست نده!</p>
            </div>

            <div
              class="bg-color-background-2/90 rounded-xl px-4 py-2 border border-color-border shadow-sm"
            >
              <div class="flex items-center gap-2">
                <span class="text-xs" style="color: var(--color-background)"
                  >پایان جشنواره :</span
                >
                <div class="flex gap-1 font-mono text-xs md:text-sm" dir="ltr">
                  <div class="text-center">
                    <div
                      class="bg-color-secondary text-white rounded px-1.5 py-0.5 font-bold"
                    >
                      {{ days }}
                    </div>
                  </div>
                  <span
                    class="text-color-primary font-bold"
                    style="color: white"
                    >:</span
                  >
                  <div class="text-center">
                    <div
                      class="bg-color-secondary text-white rounded px-1.5 py-0.5 font-bold"
                    >
                      {{ hours }}
                    </div>
                  </div>
                  <span
                    class="text-color-primary font-bold"
                    style="color: white"
                    >:</span
                  >
                  <div class="text-center">
                    <div
                      class="bg-color-secondary text-white rounded px-1.5 py-0.5 font-bold"
                    >
                      {{ minutes }}
                    </div>
                  </div>
                  <span
                    class="text-color-primary font-bold"
                    style="color: white"
                    >:</span
                  >
                  <div class="text-center">
                    <div
                      class="bg-color-secondary text-white rounded px-1.5 py-0.5 font-bold"
                    >
                      {{ seconds }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <swiper
            :modules="[Navigation]"
            :slides-per-view="1.5"
            :space-between="12"
            :navigation="false"
            :grab-cursor="true"
            :breakpoints="{
              '640': { slidesPerView: 2.5 },
              '768': { slidesPerView: 3 },
              '1024': { slidesPerView: 4 },
              '1280': { slidesPerView: 5 },
            }"
            class="offerwiper"
            style="color: var(--color-background)"
          >
            <swiper-slide v-for="offer in specialOffers" :key="offer.id">
              <ProductCard
                :product="offer"
                @click="router.push(`/products/${offer.slug || offer.id}`)"
              />
            </swiper-slide>
          </swiper>
        </div>
      </section>

      <!-- Top Sales Section -->
      <section
        v-if="topSalesProducts.length > 0"
        class="container-custom py-6"
        style="color: var(--color-background)"
      >
        <div class="relative">
          <!-- Border Top Line -->
          <div
            class="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-0.5 bg-color-secondary rounded-full"
          ></div>
          <div id="top_sales" class="rounded-2xl p-4 md:p-6 pt-7">
            <h2
              class="text-xl md:text-2xl font-bold text-center mb-4"
              style="color: var(--color-background)"
            >
              <span class="text-color-landing-dark-bg">پرفروش‌ترین</span>
              <span class="text-color-[--color-background]"> محصولات</span>
            </h2>

            <swiper
              :modules="[Navigation, Autoplay]"
              :slides-per-view="1.5"
              :space-between="12"
              :navigation="false"
              :grab-cursor="true"
              :loop="topSalesProducts.length > 5"
              :autoplay="{
                delay: 3000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }"
              :breakpoints="{
                '640': { slidesPerView: 2.5 },
                '768': { slidesPerView: 3 },
                '1024': { slidesPerView: 4 },
                '1280': { slidesPerView: 5 },
              }"
              class="top-sales-swiper"
            >
              <swiper-slide
                v-for="product in topSalesProducts"
                :key="product.id"
              >
                <ProductCard
                  :product="product"
                  @click="router.push(`/products/${product.slug}`)"
                />
              </swiper-slide>
            </swiper>
          </div>
        </div>
      </section>

      <!-- Product Category Section - اصلاح شده -->
      <section
        v-if="categoriesWithProducts.length > 0"
        class="container-custom py-6"
      >
        <div class="grid grid-cols-1 gap-8">
          <div v-for="cat in categoriesWithProducts" :key="cat.id" class="mb-8">
            <h2 class="text-xl md:text-2xl font-bold mb-4 text-right">
              <span class="text-color-secondary">{{ cat.name }}</span>
            </h2>

            <swiper
              :modules="[Navigation]"
              :slides-per-view="1.5"
              :space-between="12"
              :navigation="false"
              :grab-cursor="true"
              :breakpoints="{
                '640': { slidesPerView: 2.5 },
                '768': { slidesPerView: 3 },
                '1024': { slidesPerView: 3.5 },
                '1280': { slidesPerView: 4 },
              }"
              class="cat-products-swiper"
            >
              <swiper-slide
                v-for="product in getCategoryProducts(cat)"
                :key="product.id"
              >
                <ProductCard
                  :product="product"
                  @click="router.push(`/products/${product.slug}`)"
                />
              </swiper-slide>
            </swiper>
          </div>
        </div>
      </section>

      <!-- Blog Section -->
      <section v-if="blogPosts.length > 0" class="container-custom py-6 mt-4">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl md:text-2xl font-bold">
            <span class="text-color-secondary">آخرین</span>
            <span class="text-color-text-primary"> مقالات</span>
          </h2>
          <router-link
            to="/blog"
            class="text-color-primary hover:text-color-secondary text-xs flex items-center gap-1"
          >
            <ArrowLeft class="w-3 h-3 rotate-180" />
            مشاهده همه
          </router-link>
        </div>

        <swiper
          :modules="[Navigation]"
          :slides-per-view="1.2"
          :space-between="16"
          :navigation="false"
          :grab-cursor="true"
          :breakpoints="{
            '640': { slidesPerView: 1.5 },
            '768': { slidesPerView: 2 },
            '1024': { slidesPerView: 2.5 },
            '1280': { slidesPerView: 3 },
          }"
          class="blog-swiper"
        >
          <swiper-slide v-for="post in blogPosts" :key="post.id">
            <div
              class="bg-color-background-2 rounded-xl overflow-hidden border border-color-border hover:border-color-secondary transition-all group cursor-pointer shadow-sm h-full"
              @click="router.push(`/blog/${post.slug || post.id}`)"
            >
              <div class="relative h-32 md:h-40 overflow-hidden">
                <img
                  :src="post.image || Defaultphoto"
                  @error="handleImageError($event, post.image)"
                  :alt="post.title"
                  class="w-full h-full object-cover group-hover:scale-105 transition duration-500 pointer-events-none"
                />
                <div
                  class="absolute top-2 right-2 bg-color-secondary text-text-secondary text-[12px] font-bold px-1.5 py-0.5 rounded-full"
                >
                  {{ post.categoryName }}
                </div>
              </div>
              <div class="p-4">
                <div
                  class="flex items-center gap-2 text-[12px] text-gray-500 mb-2"
                >
                  <span class="flex items-center gap-0.5">
                    <Calendar class="w-2.5 h-2.5" /> {{ post.date }}
                  </span>
                </div>

                <h3
                  class="text-base font-bold text-color-text-primary mb-2 line-clamp-2 group-hover:text-color-secondary transition-colors min-h-[48px]"
                >
                  {{ post.title }}
                </h3>

                <p class="text-gray-500 text-xs leading-relaxed line-clamp-2">
                  {{ post.excerpt }}
                </p>
              </div>
            </div>
          </swiper-slide>
        </swiper>
      </section>
    </div>
  </div>
</template>

<script setup>
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRouter } from "vue-router";
import {
  ArrowLeft,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from "lucide-vue-next";
import ShopService from "@/api/services/shopService";
import BlogService from "@/api/services/blogService";
import ProductCard from "@/components/ProductCard.vue";
import { useToast } from "vue-toastification";

const toast = useToast();
import DefaultPhotoImg from "@/assets/Defaultphoto.png";

const router = useRouter();
const Defaultphoto = DefaultPhotoImg;

const loading = ref(true);
const error = ref(null);
const currentSlide = ref(0);
let slideInterval = null;
let timerInterval = null;

const categories = ref([]);
const activeCampaign = ref(null);
const specialOffers = ref([]);
const topSalesProducts = ref([]);
const blogPosts = ref([]);
const campaignProductIds = ref(new Set());
const campaignDiscountPercent = ref(0);
const bannersData = ref(null);

// تابع کمکی برای پردازش تصویر محصول
const processProductImage = (product) => {
  let imageUrl = "";
  
  if (Array.isArray(product.images) && product.images.length > 0) {
    // اگر images آرایه‌ای از آبجکت‌ها باشه (مثل [{url: "..."}])
    if (typeof product.images[0] === 'object' && product.images[0]?.url) {
      imageUrl = product.images[0].url;
    } else {
      // اگر مستقیم رشته باشه
      imageUrl = product.images[0];
    }
  } else if (product.image) {
    imageUrl = product.image;
  }
  
  // اضافه کردن BASE_URL اگر لازم باشه
  if (imageUrl && imageUrl.startsWith("/") && window.API_CONFIG?.BASE_URL) {
    imageUrl = window.API_CONFIG.BASE_URL + imageUrl;
  }
  
  return imageUrl;
};

// تابع اصلی برای گرفتن محصولات هر دسته
const getCategoryProducts = (cat) => {
  if (!cat.products) return [];

  const productList = Array.isArray(cat.products)
    ? cat.products
    : typeof cat.products === "object"
    ? Object.values(cat.products).flat()
    : [];

  return productList
    .filter((p) => p.status !== "draft")
    .map((product) => {
      const price = product.price || 0;
      const isCampaign = campaignProductIds.value.has(product.id);
      const discount = isCampaign
        ? campaignDiscountPercent.value
        : product.discount || 0;
      const finalPrice = price * (1 - discount / 100);

      return {
        ...product,
        image: processProductImage(product),
        original_price: price,
        final_price: Math.round(finalPrice),
        discount,
        is_on_campaign: isCampaign,
        is_available: product.status === "available",
        status_display: product.status === "unavailable" ? "ناموجود" : "موجود",
        slug: product.slug || product.id,
      };
    });
};

// Computed برای دسته‌بندی‌هایی که محصول دارند
const categoriesWithProducts = computed(() => {
  return categories.value.filter((cat) => {
    const products = getCategoryProducts(cat);
    return products.length > 0;
  });
});

// اسلایدر هندلرها
const onSwiper = (swiper) => {
  // ذخیره نمونه swiper برای استفاده‌های بعدی
};

const onSlideChange = (swiper) => {
  currentSlide.value = swiper.activeIndex;
};

// هندلرهای بنر
const hasValidImage = (banner) => {
  return !!(banner.image_url || banner.mobile_image_url);
};

const getBannerImage = (banner) => {
  if (!banner) return null;
  const isMobile = window.matchMedia("(max-width: 767px)").matches;
  if (isMobile && banner.mobile_image_url) {
    return banner.mobile_image_url;
  }
  return banner.image_url || null;
};

const handleBannerImageError = (event, banner) => {
  if (event.target && event.target.closest) {
    const bannerElement = event.target.closest("a, section > div");
    if (bannerElement) {
      bannerElement.style.display = "none";
    }
  }
};

const handleImageError = (event, originalSrc) => {
  if (event.target.src !== Defaultphoto) {
    event.target.src = Defaultphoto;
  }
};

// Computed properties برای بنرها
const heroSlides = computed(() => {
  if (bannersData.value?.banners) {
    const sliders = bannersData.value.banners
      .filter((b) => b.type === "slider" && hasValidImage(b))
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((b, index) => ({
        id: b.order || index,
        image_url: getBannerImage(b),
        title: b.title || "",
        link: b.link,
      }));
    return sliders;
  }
  return [];
});

const doubleBanners = computed(() => {
  if (bannersData.value?.banners) {
    const banners = bannersData.value.banners
      .filter(
        (b) =>
          (b.type === "small-1" || b.type === "small-2") && hasValidImage(b)
      )
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .slice(0, 2)
      .map((b) => ({
        id: b.type,
        image_url: getBannerImage(b),
        title: b.title || "",
        link: b.link,
      }));
    return banners;
  }
  return [];
});

const miniBanners = computed(() => {
  if (bannersData.value?.banners) {
    const banners = bannersData.value.banners
      .filter((b) => {
        if (b.type === "small-3" || b.type === "small-4") {
          return hasValidImage(b);
        }
        if (b.type === "small-1" || b.type === "small-2") {
          const doubleBannersCount = bannersData.value.banners.filter(
            (b2) =>
              (b2.type === "small-1" || b2.type === "small-2") &&
              hasValidImage(b2)
          ).length;
          if (doubleBannersCount < 2) {
            return hasValidImage(b);
          }
          return false;
        }
        return false;
      })
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((b) => ({
        id: b.type,
        image_url: getBannerImage(b),
        title: b.title || "",
        link: b.link,
      }));

    return banners.slice(0, 4);
  }
  return [];
});

const staticBanner = computed(() => {
  if (bannersData.value?.banners) {
    const staticB = bannersData.value.banners.find(
      (b) => b.type === "static" && hasValidImage(b)
    );
    if (staticB) {
      return {
        image_url: getBannerImage(staticB),
        title: staticB.title || "",
        link: staticB.link,
      };
    }
  }
  return null;
});

// Timer Logic
const targetDate = ref(null);

const calculateTimeLeft = () => {
  if (!targetDate.value) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  const now = new Date().getTime();
  const distance = targetDate.value - now;
  if (distance < 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(distance / (1000 * 60 * 60 * 24)),
    hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((distance % (1000 * 60)) / 1000),
  };
};

const timeLeft = ref(calculateTimeLeft());
const days = computed(() => String(timeLeft.value.days).padStart(2, "0"));
const hours = computed(() => String(timeLeft.value.hours).padStart(2, "0"));
const minutes = computed(() => String(timeLeft.value.minutes).padStart(2, "0"));
const seconds = computed(() => String(timeLeft.value.seconds).padStart(2, "0"));

// تابع اصلی initData
const initData = async () => {
  loading.value = true;
  error.value = null;

  try {
    // دسته‌بندی‌ها و محصولات همزمان
    const [categoriesResponse, productsResponse] = await Promise.all([
      ShopService.getShopCategory(),
      ShopService.getShopProducts(),
    ]);

    // ذخیره اطلاعات کامل دسته‌بندی‌ها (شامل slug)
    const categoriesFromAPI = Array.isArray(categoriesResponse)
      ? categoriesResponse
      : categoriesResponse?.data || [];

    const categoryInfoMap = new Map();
    categoriesFromAPI.forEach((cat) => {
      if (cat.name) {
        categoryInfoMap.set(cat.name, {
          icon: cat.icon || cat.image || null,
          slug: cat.slug || null,
          id: cat.id,
          name: cat.name,
        });
      }
      if (cat.id) {
        categoryInfoMap.set(cat.id, {
          icon: cat.icon || cat.image || null,
          slug: cat.slug || null,
          id: cat.id,
          name: cat.name,
        });
      }
    });

    // محصولات
    const allProducts = Array.isArray(productsResponse)
      ? productsResponse
      : productsResponse?.data || [];

    const availableProducts = allProducts.filter((p) => p.status !== "draft");

    const processedProducts = availableProducts.map((p) => ({
      ...p,
      status_display:
        p.status === "unavailable"
          ? "ناموجود"
          : p.status_display || " جزئیات محصول",
      is_available: p.status === "available",
    }));

    // گروه‌بندی بر اساس category_name
    const categoryMap = new Map();
    processedProducts.forEach((product) => {
      const catName = product.category_name || "بدون دسته‌بندی";
      if (!categoryMap.has(catName)) categoryMap.set(catName, []);
      categoryMap.get(catName).push(product);
    });

    const uniqueCategories = [];
    let catId = 1;
    categoryMap.forEach((products, catName) => {
      const catInfo = categoryInfoMap.get(catName) || {};
      uniqueCategories.push({
        id: catInfo.id || catId++,
        name: catName,
        slug: catInfo.slug || null,
        icon: catInfo.icon || null,
        products: products,
      });
    });

    categories.value = uniqueCategories;

    // بنرها
    try {
      const bannerResult = await ShopService.getStoreBanners();
      bannersData.value =
        bannerResult.success && bannerResult.data?.banners
          ? bannerResult.data
          : null;
    } catch (bannerErr) {
      console.error("Banner error:", bannerErr);
      bannersData.value = null;
    }

    // کمپین
    const campaignsResponse = await ShopService.getShopCampain();
    const campaignsData = campaignsResponse.data || campaignsResponse;

    let campaignData = null;
    if (Array.isArray(campaignsData)) {
      campaignData = campaignsData.find((c) => c.is_active) || campaignsData[0];
    } else if (campaignsData && typeof campaignsData === "object") {
      campaignData = campaignsData;
    }

    if (campaignData) {
      activeCampaign.value = campaignData;
      campaignProductIds.value = new Set();
      const newSpecialOffers = [];

      if (
        campaignData.products &&
        typeof campaignData.products === "object" &&
        !Array.isArray(campaignData.products)
      ) {
        for (const categoryName in campaignData.products) {
          const itemsInCategory = campaignData.products[categoryName];
          if (Array.isArray(itemsInCategory)) {
            itemsInCategory.forEach((p) => {
              if (p.status === "draft") return;
              campaignProductIds.value.add(p.id);
              const originalPrice = p.price || 0;
              const finalPrice =
                originalPrice * (1 - campaignData.discount / 100);
              
              const imageUrl = processProductImage(p);
              
              newSpecialOffers.push({
                id: p.id,
                slug: p.slug,
                name: p.name,
                title: p.name,
                final_price: Math.round(finalPrice),
                price: originalPrice,
                discount: campaignData.discount,
                image: imageUrl,
                is_on_campaign: true,
                quantity: 1,
                is_available: p.status === "available",
                status_display:
                  p.status === "unavailable" ? "ناموجود" : "موجود",
              });
            });
          }
        }
      }

      specialOffers.value = [...newSpecialOffers];
      campaignDiscountPercent.value = campaignData.discount || 0;

      if (campaignData.date_remind) {
        const now = new Date();
        targetDate.value = new Date(
          now.getTime() + campaignData.date_remind * 1000
        ).getTime();
      } else {
        const futureDate = new Date();
        futureDate.setDate(futureDate.getDate() + 2);
        targetDate.value = futureDate.getTime();
      }
    } else {
      activeCampaign.value = null;
      campaignProductIds.value = new Set();
      campaignDiscountPercent.value = 0;
      specialOffers.value = [];
      targetDate.value = null;
    }

    // پرفروش‌ترین
    const topSalesResponse = await ShopService.getShopTopSales();
    const productsToUse = Array.isArray(topSalesResponse)
      ? topSalesResponse
      : [];

    topSalesProducts.value = productsToUse
      .filter((p) => p.status !== "draft")
      .map((p) => {
        const price = p.price || 0;
        let discount = 0;
        let isCampaignApplied = false;

        if (
          campaignProductIds.value.has(p.id) &&
          campaignDiscountPercent.value > 0
        ) {
          discount = campaignDiscountPercent.value;
          isCampaignApplied = true;
        } else if (p.discount > 0) {
          discount = p.discount;
        }

        return {
          ...p,
          image: processProductImage(p),
          final_price: Math.round(price * (1 - discount / 100)),
          discount,
          is_on_campaign: isCampaignApplied,
          is_available: p.status === "available",
          status_display: p.status === "unavailable" ? "ناموجود" : "موجود",
        };
      });

    // بلاگ
    if (BlogService && typeof BlogService.getAllBlog === "function") {
      const blogsResponse = await BlogService.getAllBlog();
      blogPosts.value = Array.isArray(blogsResponse)
        ? blogsResponse.map((blog) => {
            let imageUrl = blog.images || [];
            if (imageUrl.length >= 1) imageUrl = imageUrl?.[0]?.url ?? null;
            return {
              id: blog.id,
              slug: blog.slug,
              title: blog.title,
              excerpt:
                blog.excerpt ||
                (blog.text ? blog.text.substring(0, 100) + "..." : ""),
              image: imageUrl,
              date: blog.created_at
                ? new Date(blog.created_at).toLocaleDateString("fa-IR")
                : "",
              author: blog.author_name || "نویسنده",
              categoryName: blog.category_name || "وبلاگ",
            };
          })
        : [];
    }
  } catch (err) {
    console.error("Error in initData:", err);
    if (err.response) {
      const status = err.response.status;
      if (status === 404) error.value = "اطلاعات فروشگاه یافت نشد.";
      else if (status === 500) error.value = "خطایی در ارتباط با سرور رخ داد.";
      else if (status === 401 || status === 403)
        error.value = "لطفاً ابتدا وارد حساب کاربری خود شوید.";
      else error.value = err.response.data?.message || "خطای ناشناخته.";
    } else if (err.request) {
      error.value = "اتصال به سرور برقرار نشد.";
    } else {
      error.value = err.message || "خطایی در پردازش درخواست رخ داد.";
    }
  } finally {
    loading.value = false;
  }
};

const goToLoginOrHome = () => {
  router.push("/login");
};

const getFullUrl = (link) => {
  if (!link) return "#";
  if (link.startsWith("http://") || link.startsWith("https://")) {
    return link;
  }
  const path = link.startsWith("/") ? link : "/" + link;
  return "https://mmd1.shopecom.ir" + path;
};

onMounted(() => {
  initData();
  slideInterval = setInterval(() => {
    if (heroSlides.value.length > 0) {
      currentSlide.value = (currentSlide.value + 1) % heroSlides.value.length;
    }
  }, 5000);
  timerInterval = setInterval(() => {
    timeLeft.value = calculateTimeLeft();
  }, 1000);
});

onUnmounted(() => {
  if (slideInterval) clearInterval(slideInterval);
  if (timerInterval) clearInterval(timerInterval);
});
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.animate-slide-up {
  animation: slideUp 0.4s ease-out;
}

.animate-fade-in {
  animation: fadeIn 0.5s ease-in-out;
}

/* حذف کامل اسکرول بار */
.scrollbar-none {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-none::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.select-none {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.top-sales-swiper {
  padding: 10px 0 !important;
}

.top-sales-swiper :deep(.swiper-button-next),
.top-sales-swiper :deep(.swiper-button-prev) {
  display: none !important;
}

.category-swiper {
  padding: 10px 0 !important;
}

.category-swiper :deep(.swiper-button-next),
.category-swiper :deep(.swiper-button-prev) {
  display: none !important;
}

.cat-products-swiper {
  padding: 10px 0 !important;
}

.cat-products-swiper :deep(.swiper-button-next),
.cat-products-swiper :deep(.swiper-button-prev) {
  display: none !important;
}

.blog-swiper {
  padding: 10px 0 !important;
}

.blog-swiper :deep(.swiper-button-next),
.blog-swiper :deep(.swiper-button-prev) {
  display: none !important;
}

.offers-swiper {
  padding: 10px 0 !important;
}

.offers-swiper :deep(.swiper-button-next),
.offers-swiper :deep(.swiper-button-prev) {
  display: none !important;
}

@media screen and (max-width: 720px) {
  .container-custom .flex.justify-start.gap-4 {
    gap: 0.75rem;
    padding-left: 0rem;
    padding-right: 0rem;
  }

  .container-custom .flex.justify-start.gap-4 > div {
    min-width: 100px;
    padding: 0.75rem;
  }
}

#top_sales {
  background-color: var(--color-secondary);
  border: 2px var(--color-secondary) solid;
}
</style>